/**
 * Token storage and rotation.
 *  - The ACCESS token lives only in memory (module variable): an XSS-stolen page reload cannot read it from storage.
 *  - The REFRESH token lives in localStorage (guarded by try/catch) so a reload can restore the session.
 *  - Every refresh ROTATES the refresh token: the new one is saved as soon as it arrives.
 */
import type { SessionUser, TokenResponse } from '@lumina/types';
import { api, configureApi, isApiError } from '../api/client';
import { readStoredString, removeStored, writeStoredString } from '../storage';

export const REFRESH_KEY = 'lumina.refresh.v1';

let accessToken: string | null = null;

export type AuthEvent = { type: 'refreshed'; user: SessionUser } | { type: 'signed-out' };
const listeners = new Set<(event: AuthEvent) => void>();

export function subscribeAuth(listener: (event: AuthEvent) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function emit(event: AuthEvent): void {
  listeners.forEach((l) => l(event));
}

export const getAccessToken = (): string | null => accessToken;
export const getRefreshToken = (): string | null => readStoredString(REFRESH_KEY);

/** Store a fresh token pair (login or refresh). */
export function applyTokens(response: Pick<TokenResponse, 'accessToken' | 'refreshToken'>): void {
  accessToken = response.accessToken;
  writeStoredString(REFRESH_KEY, response.refreshToken);
}

export function clearTokens(): void {
  accessToken = null;
  removeStored(REFRESH_KEY);
}

/**
 * Exchange the stored refresh token for a new pair.
 * Resolves the new access token; null when there is no usable session; throws on transient failures
 * (offline, 5xx) so the caller keeps the session and can retry.
 */
export async function performRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }
  try {
    const response = await api.post<TokenResponse>('auth/refresh', { refreshToken }, { anonymous: true });
    applyTokens(response);
    emit({ type: 'refreshed', user: response.user });
    return response.accessToken;
  } catch (error) {
    if (isApiError(error) && (error.status === 400 || error.status === 401 || error.status === 403)) {
      clearTokens();
      return null;
    }
    throw error;
  }
}

/** Connects the HTTP client to this token store. Idempotent. */
export function installAuthHandlers(): void {
  configureApi({
    getAccessToken,
    refresh: performRefresh,
    onAuthFailure: () => {
      clearTokens();
      emit({ type: 'signed-out' });
    },
  });
}
